import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d52pf82bfdatfusc40pg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact" className="relative h-[70vh] w-full overflow-hidden">
      <div className="absolute inset-0">
        <img 
          src="https://public.readdy.ai/ai/img_res/001cc977baecf0a87c2a4a696aa8db03.jpg"
          alt="Contact Background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
      </div>

      <div className="relative h-full flex items-center justify-between px-20 max-w-7xl mx-auto">
        <div>
          <h2 className="text-8xl font-bold text-white leading-none mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            let's connect
          </h2>
        </div>

        <div className="max-w-md">
          <p className="text-xl text-white font-light leading-relaxed mb-8">
            I'd love to hear from you. Whether you're a reader,<br />
            fellow writer, or media professional, reach out below.
          </p>
        </div>
      </div>

      <div className="absolute bottom-24 left-20">
        <button 
          onClick={() => {
            const modal = document.getElementById('contact-modal');
            if (modal) modal.classList.remove('hidden');
          }}
          className="group flex items-center gap-0 bg-white rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 cursor-pointer overflow-hidden whitespace-nowrap"
        >
          <span className="text-[#2C2C2C] text-base font-medium pl-8 pr-6">Get in Touch</span>
          <div className="w-11 h-11 bg-[#2C2C2C] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
            <i className="ri-arrow-right-line text-white text-xl"></i>
          </div>
        </button>
      </div>

      {/* Contact Form Modal */}
      <div id="contact-modal" className="hidden fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-8">
        <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-12">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-4xl font-bold text-[#2C2C2C]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Contact Me
              </h3>
              <button 
                onClick={() => {
                  const modal = document.getElementById('contact-modal');
                  if (modal) modal.classList.add('hidden');
                }}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#F8F6F3] transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-[#2C2C2C]"></i>
              </button>
            </div>

            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-[#E5E5E5] rounded-lg focus:outline-none focus:border-[#C9A961] transition-colors"
                  placeholder="Enter your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-[#E5E5E5] rounded-lg focus:outline-none focus:border-[#C9A961] transition-colors"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-[#E5E5E5] rounded-lg focus:outline-none focus:border-[#C9A961] transition-colors"
                  placeholder="What's this about?"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 border border-[#E5E5E5] rounded-lg focus:outline-none focus:border-[#C9A961] transition-colors resize-none"
                  placeholder="Your message (max 500 characters)"
                />
                <div className="text-xs text-[#6B6B6B] mt-1 text-right">
                  {formData.message.length}/500 characters
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="p-4 bg-[#D4E5D4] rounded-lg">
                  <p className="text-[#2C2C2C] text-sm">Thank you for your message! I'll get back to you soon.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-red-600 text-sm">Something went wrong. Please try again.</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || formData.message.length > 500}
                className="w-full py-4 bg-[#1A2332] text-white rounded-full font-medium hover:bg-[#1A2332]/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
